/**
 * PATH: apps/qa/src/components/inline-report/SignOffPanel.tsx
 * PURPOSE: Enhanced sign-off panel with finger signature, signer details, and optional photo.
 *
 * FEATURES:
 *   - Canvas-based finger/mouse signature capture
 *   - Name, designation, company fields
 *   - Optional profile photo upload
 *   - Stores all data in ReportDraft.sign_off_data
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { reportDraftApi } from '@/lib/api'
import type { SignOffData } from '@/lib/api/reports'
import {
  PenTool, Clock, X, Loader2, Trash2, Pen,
  Camera, Building2, Briefcase, User,
} from 'lucide-react'

interface SignOffPanelProps {
  draftId: string
  draftVersion: number
  inspectionId: string
  onClose: () => void
}

// ── Inline Signature Pad ────────────────────────────────────
// Lightweight canvas for drawing a finger/mouse signature.

function cropInkDataUrl(canvas: HTMLCanvasElement): string {
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas.toDataURL('image/png')
  const { width, height } = canvas
  const data = ctx.getImageData(0, 0, width, height).data
  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] < 16) continue
      if (x < minX) minX = x
      if (y < minY) minY = y
      if (x > maxX) maxX = x
      if (y > maxY) maxY = y
    }
  }
  if (maxX < 0 || (maxX - minX + 1 === width && maxY - minY + 1 === height)) {
    return canvas.toDataURL('image/png')
  }
  const cropW = maxX - minX + 1
  const cropH = maxY - minY + 1
  const out = document.createElement('canvas')
  out.width = cropW
  out.height = cropH
  out.getContext('2d')?.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH)
  return out.toDataURL('image/png')
}

function InlineSignaturePad({
  onCapture,
  onClear,
}: {
  onCapture: (dataUrl: string) => void
  onClear: () => void
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [hasSignature, setHasSignature] = useState(false)

  const getCtx = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return null
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.strokeStyle = '#1a1a1a'
      ctx.lineWidth = 2.5
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    }
    return ctx
  }, [])

  const coords = (e: React.MouseEvent | React.TouchEvent) => {
    const rect = canvasRef.current!.getBoundingClientRect()
    if ('touches' in e)
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    const ctx = getCtx()
    if (!ctx) return
    setIsDrawing(true)
    setHasSignature(true)
    const { x, y } = coords(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return
    const ctx = getCtx()
    if (!ctx) return
    const { x, y } = coords(e)
    ctx.lineTo(x, y)
    ctx.stroke()
  }

  const stop = () => {
    if (isDrawing && hasSignature && canvasRef.current)
      onCapture(cropInkDataUrl(canvasRef.current))
    setIsDrawing(false)
  }

  const clear = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    canvas.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height)
    setHasSignature(false)
    onClear()
  }

  // Size canvas to its container on mount
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    canvas.width = rect.width * (window.devicePixelRatio || 1)
    canvas.height = rect.height * (window.devicePixelRatio || 1)
    const ctx = canvas.getContext('2d')
    if (ctx) ctx.scale(window.devicePixelRatio || 1, window.devicePixelRatio || 1)
  }, [])

  return (
    <div className="space-y-1.5">
      <label className="text-[10px] sm:text-xs font-medium text-muted-foreground flex items-center gap-1">
        <Pen className="w-3 h-3" />
        Signature <span className="text-destructive">*</span>
      </label>
      <div className="relative border-2 border-dashed border-border rounded-xl overflow-hidden bg-white dark:bg-slate-900">
        <canvas
          ref={canvasRef}
          className="w-full cursor-crosshair touch-none"
          style={{ height: 120 }}
          onMouseDown={start}
          onMouseMove={draw}
          onMouseUp={stop}
          onMouseLeave={stop}
          onTouchStart={start}
          onTouchMove={draw}
          onTouchEnd={stop}
        />
        {!hasSignature && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-muted-foreground text-xs flex items-center gap-1.5">
              <Pen className="w-3.5 h-3.5" />
              Draw your signature here
            </span>
          </div>
        )}
      </div>
      {hasSignature && (
        <button
          onClick={clear}
          className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground hover:text-destructive transition-colors"
        >
          <Trash2 className="w-3 h-3" /> Clear
        </button>
      )}
    </div>
  )
}

// ── Main Panel ──────────────────────────────────────────────

export function SignOffPanel({ draftId, draftVersion, inspectionId, onClose }: SignOffPanelProps) {
  const queryClient = useQueryClient()

  // Pre-fill from localStorage (last-used values) and fall back to empty
  const [signerName, setSignerName] = useState(() => {
    try { return localStorage.getItem('qa_signoff_name') || '' } catch { return '' }
  })
  const [designation, setDesignation] = useState(() => {
    try { return localStorage.getItem('qa_signoff_designation') || '' } catch { return '' }
  })
  const [company, setCompany] = useState(() => {
    try { return localStorage.getItem('qa_signoff_company') || '' } catch { return '' }
  })
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null)
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null)

  const photoInputRef = useRef<HTMLInputElement>(null)

  // Mutation: save sign-off data + change status to 'final'
  const signOffMutation = useMutation({
    mutationFn: () => {
      const signOffData: SignOffData = {
        signer_name: signerName.trim(),
        designation: designation.trim(),
        company: company.trim(),
        signature_data_url: signatureDataUrl!,
        ...(photoDataUrl ? { photo_data_url: photoDataUrl } : {}),
        signed_at: new Date().toISOString(),
      }
      // Persist to localStorage for next sign-off
      try {
        localStorage.setItem('qa_signoff_name', signerName.trim())
        localStorage.setItem('qa_signoff_designation', designation.trim())
        localStorage.setItem('qa_signoff_company', company.trim())
      } catch { /* quota exceeded — ignore */ }
      return reportDraftApi.updateDraft(draftId, { status: 'final', sign_off_data: signOffData, expected_version: draftVersion })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qa-report-draft', draftId] })
      queryClient.invalidateQueries({ queryKey: ['qa-report-drafts-for-inspection', inspectionId] })
      onClose()
    },
  })

  // Photo upload handler — reads file as data URL
  const handlePhotoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 5 * 1024 * 1024) {
      alert('Photo must be under 5 MB')
      return
    }
    const reader = new FileReader()
    reader.onload = () => setPhotoDataUrl(reader.result as string)
    reader.readAsDataURL(file)
  }, [])

  const isValid = signerName.trim() && signatureDataUrl

  return (
    <div className="p-3 sm:p-4 bg-emerald-50/50 dark:bg-emerald-900/10 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs sm:text-sm font-semibold text-foreground flex items-center gap-1.5">
          <PenTool className="w-3.5 h-3.5 text-emerald-600" />
          Sign Off
        </h3>
        <button onClick={onClose} className="p-1 hover:bg-accent rounded">
          <X className="w-3.5 h-3.5 text-muted-foreground" />
        </button>
      </div>

      {/* Signature Canvas */}
      <InlineSignaturePad
        onCapture={setSignatureDataUrl}
        onClear={() => setSignatureDataUrl(null)}
      />

      {/* Signer Details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {/* Name */}
        <div className="sm:col-span-2">
          <label className="text-[10px] sm:text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
            <User className="w-3 h-3" />
            Full Name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            placeholder="Your full name"
            className="w-full text-xs sm:text-sm px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>

        {/* Designation */}
        <div>
          <label className="text-[10px] sm:text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
            <Briefcase className="w-3 h-3" />
            Designation
          </label>
          <input
            type="text"
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
            placeholder="e.g. Senior Inspector"
            className="w-full text-xs sm:text-sm px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>

        {/* Company */}
        <div>
          <label className="text-[10px] sm:text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
            <Building2 className="w-3 h-3" />
            Company
          </label>
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="e.g. Bravada"
            className="w-full text-xs sm:text-sm px-3 py-2 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>
      </div>

      {/* Photo (optional) */}
      <div>
        <label className="text-[10px] sm:text-xs font-medium text-muted-foreground flex items-center gap-1 mb-1">
          <Camera className="w-3 h-3" />
          Photo <span className="text-muted-foreground/60">(optional)</span>
        </label>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={handlePhotoChange}
          className="hidden"
        />
        {photoDataUrl ? (
          <div className="flex items-center gap-2">
            <img
              src={photoDataUrl}
              alt="Signer photo"
              className="w-10 h-10 rounded-full object-cover border-2 border-emerald-500/30"
            />
            <button
              onClick={() => { setPhotoDataUrl(null); if (photoInputRef.current) photoInputRef.current.value = '' }}
              className="text-[10px] sm:text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1"
            >
              <Trash2 className="w-3 h-3" /> Remove
            </button>
          </div>
        ) : (
          <button
            onClick={() => photoInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] sm:text-xs rounded-lg border border-dashed border-input text-muted-foreground hover:border-emerald-500/50 hover:text-foreground transition-colors"
          >
            <Camera className="w-3.5 h-3.5" />
            Add photo
          </button>
        )}
      </div>

      {/* Date */}
      <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
        <Clock className="w-3 h-3" />
        Date: {new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
      </div>

      {/* Confirm Button */}
      <button
        onClick={() => { if (isValid) signOffMutation.mutate() }}
        disabled={!isValid || signOffMutation.isPending}
        className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-emerald-500 text-white text-xs sm:text-sm font-medium hover:bg-emerald-600 disabled:opacity-50 transition-colors"
      >
        {signOffMutation.isPending ? (
          <><Loader2 className="w-3.5 h-3.5 animate-spin" />Signing...</>
        ) : (
          <><PenTool className="w-3.5 h-3.5" />Confirm Sign-Off</>
        )}
      </button>

      {!isValid && signerName.trim() && !signatureDataUrl && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400 text-center">
          Please draw your signature above to continue
        </p>
      )}
    </div>
  )
}
